import { useCallback } from 'react';

import { apiSetABExperimentBucket } from '#/api/abExperiment/apiSetABExperimentBucket';
import type {
  ApiABExperiment,
  ApiABExperimentBucketForId,
  ApiABExperimentId,
} from '#/api/abExperiment/types/ApiABExperiment';
import type { ApiCountryCode } from '#/api/types/ApiCountryCode';
import type { ApiGuessedLocation } from '#/api/types/ApiGuessedLocation';
import { apiFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import type {
  ApiEnvironment,
  ApiUser,
  ApiUserEnvironment,
} from '#/api/userEnvironment/types/ApiUserEnvironment';
import { assignAnalyticsAbExperiments } from '#/utils/analytics/abExperiments/analyticsAbExperiments';
import { assignAnalyticsAbExperimentBucket } from '#/utils/analytics/abExperiments/assignAnalyticsAbExperimentBucket';
import { createZustandContextStore } from '#/zustand-stores/utils/createZustandContextStore';

export type ClientEnvironment = {
  guessedLocation: ApiGuessedLocation;
  showAcceptCookies: boolean;
  abExperiments: ApiABExperiment[];
  extraData: Record<string, string>;
};

export type IsomorphicEnvironment = {
  isEU: boolean;
  isUS: boolean;
  countryCode: ApiCountryCode;
};

type UserEnvironmentContextState = {
  user: ApiUser | null;
  clientEnvironment: ClientEnvironment | null;
  isomorphicEnvironment: IsomorphicEnvironment;
  actionsWhileLoggedOutProcessedAt: number | null;
};

const {
  ZustandContextProvider,
  useZustandContextStore,
  useZustandContextSelector,
} = createZustandContextStore<UserEnvironmentContextState>({
  fallbackState: {
    user: null,
    clientEnvironment: null,
    isomorphicEnvironment: {
      isEU: false,
      isUS: true,
      countryCode: 'US',
    },
    actionsWhileLoggedOutProcessedAt: null,
  },

  onCreate: (store) => {
    store.subscribe((newState) => {
      if (newState.clientEnvironment) {
        assignAnalyticsAbExperiments(newState.clientEnvironment.abExperiments);
      }
    });
  },
});

export const UserEnvironmentContextProvider = ZustandContextProvider;

// Helpers

export function splitApiEnvironmentIntoContextParts(
  environment: ApiEnvironment,
) {
  return {
    clientEnvironment: {
      guessedLocation: environment.guessedLocation,
      showAcceptCookies: environment.showAcceptCookies,
      abExperiments: environment.abExperiments,
      extraData: environment.extraData,
    } satisfies ClientEnvironment,

    isomorphicEnvironment: {
      isUS: environment.isUS,
      isEU: environment.isEU,
      countryCode: environment.guessedLocation.countryCode,
    } satisfies IsomorphicEnvironment,
  };
}

// Hooks

export function useUser(): { user: ApiUser | null } {
  const user = useZustandContextSelector((state) => state.user);
  return { user };
}

export function useLoggedIn(): { loggedIn: boolean } {
  const loggedIn = useZustandContextSelector((state) => Boolean(state.user));
  return { loggedIn };
}

export function useClientEnvironment() {
  const clientEnvironment = useZustandContextSelector(
    (state) => state.clientEnvironment,
  );
  return { clientEnvironment };
}

export function useIsomorphicEnvironment() {
  const isomorphicEnvironment = useZustandContextSelector(
    (state) => state.isomorphicEnvironment,
  );
  return { isomorphicEnvironment };
}

export function useActionsWhileLoggedOutProcessedAt() {
  const actionsWhileLoggedOutProcessedAt = useZustandContextSelector(
    (state) => state.actionsWhileLoggedOutProcessedAt,
  );
  return { actionsWhileLoggedOutProcessedAt };
}

export function useUserEnvironmentActions() {
  const store = useZustandContextStore();

  const fetchUserEnvironment = useCallback(async () => {
    const { user, environment } = await apiFetchUserEnvironment();

    const { clientEnvironment, isomorphicEnvironment } =
      splitApiEnvironmentIntoContextParts(environment);

    store.setState({ user, clientEnvironment, isomorphicEnvironment });

    return { user, environment };
  }, [store]);

  const setUserEnvironment = useCallback(
    (userEnvironment: ApiUserEnvironment) => {
      const { clientEnvironment, isomorphicEnvironment } =
        splitApiEnvironmentIntoContextParts(userEnvironment.environment);

      store.setState({
        user: userEnvironment.user,
        clientEnvironment,
        isomorphicEnvironment,
      });
    },
    [store],
  );

  const updateUserPartially = useCallback(
    (partialUser: Partial<ApiUser>) => {
      const currentState = store.getState();
      if (currentState.user) {
        store.setState({ user: { ...currentState.user, ...partialUser } });
      }
    },
    [store],
  );

  const updateEnvironmentPartially = useCallback(
    (partialEnvironment: Partial<ApiEnvironment>) => {
      const currentState = store.getState();
      if (currentState.clientEnvironment) {
        store.setState({
          clientEnvironment: {
            ...currentState.clientEnvironment,
            ...partialEnvironment,
          },
        });
      }
    },
    [store],
  );

  const setABExperimentBucket = useCallback(
    async <TExperimentId extends ApiABExperimentId>(
      experimentId: TExperimentId,
      bucket: ApiABExperimentBucketForId<TExperimentId>,
    ) => {
      assignAnalyticsAbExperimentBucket(experimentId, bucket);

      const abExperiments = await apiSetABExperimentBucket(
        experimentId,
        bucket,
      );

      assignAnalyticsAbExperiments(abExperiments);
      updateEnvironmentPartially({ abExperiments });
    },
    [updateEnvironmentPartially],
  );

  const markActionsWhileLoggedOutProcessed = useCallback(() => {
    store.setState({ actionsWhileLoggedOutProcessedAt: new Date().getTime() });
  }, [store]);

  return {
    setUserEnvironment,
    setState: store.setState,
    fetchUserEnvironment,
    updateUserPartially,
    updateEnvironmentPartially,
    setABExperimentBucket,
    markActionsWhileLoggedOutProcessed,
  };
}
